import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Fade,
  Grid,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
  Alert,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  Email as EmailIcon,
  LockReset,
  MarkEmailRead,
  Send as SendIcon,
  ShieldOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import { useRequestPasswordReset } from "../../hooks/auth";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH, RESET_PASSWORD_ROUTE_PATH } from "../../utils/constants/AppConstants";
import BrandIcon from "../../components/common/BrandIcon";

const ForgotPasswordPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const { mutate: requestPasswordReset, isPending: isSubmitting } = useRequestPasswordReset();

  const isValidEmail = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    
    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${RESET_PASSWORD_ROUTE_PATH}`;
    const callbackUrl = new URL(path, window.location.origin).toString();
    
    requestPasswordReset(
      { email: email.trim(), callbackUrl },
      {
        onSuccess: () => {
          setMessage("If an account exists for this email, a reset link has been sent.");
          const endsAt = Date.now() + 60_000; // 60s cooldown to avoid spam
          setCooldownEndsAt(endsAt);
          setSecondsLeft(60);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.userMessage || err.message);
          } else {
            setError("Could not send reset instructions. Please try again.");
          }
        }
      }
    );
  };

  useEffect(() => {
    if (!cooldownEndsAt) return;
    const interval = setInterval(() => {
      const left = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        setCooldownEndsAt(null);
        clearInterval(interval);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [cooldownEndsAt]);

  const isCooldown = cooldownEndsAt !== null;

  const webmailLink = useMemo(() => {
    const domain = (email.split("@")[1] || "").toLowerCase();
    if (domain.includes("gmail.")) return "https://mail.google.com/";
    if (domain.includes("outlook.") || domain.includes("live.") || domain.includes("hotmail.")) return "https://outlook.live.com/mail/";
    if (domain.includes("yahoo.")) return "https://mail.yahoo.com/";
    if (domain.includes("proton.")) return "https://mail.proton.me/";
    return "";
  }, [email]);

  const inputSx = {
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiOutlinedInput-root": {
      background: alpha(theme.palette.background.paper, 0.62),
      borderRadius: theme.radii?.medium ?? 10,
      color: theme.palette.text.primary,
      transition: "all 0.22s ease",
      "& input": { color: theme.palette.text.primary },
      "& input:-webkit-autofill": {
        WebkitTextFillColor: theme.palette.text.primary,
        caretColor: theme.palette.text.primary,
        WebkitBoxShadow: `0 0 0 100px ${alpha(theme.palette.background.paper, 0.62)} inset`,
        borderRadius: theme.radii?.medium ?? 10,
        transition: "background-color 9999s ease-out, color 9999s ease-out",
      },
      "& fieldset": { borderColor: alpha(theme.palette.text.primary, 0.1) },
      "&:hover fieldset": { borderColor: alpha(theme.palette.text.primary, 0.22) },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main, borderWidth: 2 },
      "&.Mui-focused": { boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.2)}` },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 18% 16%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 38%), ${theme.palette.background.default}`,
        display: "flex",
        alignItems: "center",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={700}>
          <SurfaceCard
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(8px)",
              boxShadow: "0 14px 44px rgba(2, 6, 23, 0.55)",
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: { xs: 2.6, sm: 3.2, md: 3.4 },
                  borderRight: { md: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` },
                  borderBottom: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, md: "none" },
                  background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.background.paper, 0.35)})`,
                }}
              >
                <Box sx={{ mb: 2.2 }}>
                  <BrandIcon size={48} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                  }}
                >
                  Reset your password
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, maxWidth: 360 }}>
                  Enter your account email and we will send a secure password reset link.
                </Typography>

                <Stack spacing={1.05} sx={{ mt: 2.2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <LockReset fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">Request reset instructions</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <MarkEmailRead fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">Open your email and follow the link</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <ShieldOutlined fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">Set a new secure password</Typography>
                  </Stack>
                </Stack>

                <Chip
                  icon={<TimerOutlined />}
                  label="Resend available every 60s"
                  sx={{
                    mt: 2.2,
                    borderRadius: "10px",
                    color: theme.palette.primary.light,
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={7} sx={{ p: { xs: 2.6, sm: 3.2, md: 3.8 } }}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.1 }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                    <SendIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Send reset email
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      We will send instructions to your inbox.
                    </Typography>
                  </Box>
                </Stack>

                {message && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} role="status" aria-live="polite">
                    {message}
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    id="email"
                    label="Email address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                    sx={{ mb: 2.3, ...inputSx }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!isValidEmail || isSubmitting || isCooldown}
                    startIcon={<SendIcon />}
                    sx={{
                      py: 1.35,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      borderRadius: theme.radii?.medium ?? 10,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                      },
                    }}
                  >
                    {isCooldown ? `Resend available in ${secondsLeft}s` : 'Send reset link'}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  Tip: Check your spam or junk folder if the email does not arrive within a few minutes.
                </Typography>

                {webmailLink && message && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.1 }}>
                    Quick open: <MuiLink href={webmailLink} target="_blank" rel="noopener noreferrer">open your inbox</MuiLink>
                  </Typography>
                )}

                <Box sx={{ textAlign: "center", mt: 2.2 }}>
                  <Button variant="text" startIcon={<ArrowBack />} onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                    Back to sign in
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SurfaceCard>
        </Fade>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;


